import React, { useEffect, useRef, useState } from "react";
import { Button, Form, Image, Modal } from "react-bootstrap";
import Select from "react-select";
import ReactQuill, { Quill } from "react-quill";
import Files from "react-files";
import "react-quill/dist/quill.snow.css";
import IconPlus from "../../../../assets/Files-icon.svg";
import CancleIcon from "../../../../assets/CancelIcon.svg";
import ImageBackground from "../../../../assets/ImageBackground.svg";
import LinkedinIcon from "../../../../assets/frontPageIcons/Vector.svg";
import BackIcon from "../../../../sass/styling/assets/Vector.svg";
import { FILE_SIZE_LIMIT, IMAGE_FORMAT } from "../../../../constant";
import { LoaderPage } from "../../../../component/Loader";
import PreviewArticle from "../../../articleDetail/PreviewArticle";
import MagicUrl from "quill-magic-url";
Quill.register("modules/magicUrl", MagicUrl);

const quillModules = {
  toolbar: {
    container: "#cms-articles-quill-toolbar"
  },
  magicUrl: true
};

const quillFormats = [
  "bold",
  "italic",
  "underline",
  "strike",
  "header",
  "size",
  "list",
  "bullet",
  "indent",
  "link",
  "script"
];

const ArticleContentToolbar = () => (
  <div id="cms-articles-quill-toolbar" className="cms-articles-quill-toolbar">
    <span className="ql-formats">
      <button type="button" className="ql-bold" aria-label="Bold" />
      <button type="button" className="ql-italic" aria-label="Italic" />
      <button type="button" className="ql-underline" aria-label="Underline" />
      <button type="button" className="ql-strike" aria-label="Strikethrough" />
    </span>
    <span className="ql-formats cms-articles-heading-buttons">
      <button
        type="button"
        className="ql-header"
        value="1"
        aria-label="Heading 1"
      >
        H1
      </button>
      <button
        type="button"
        className="ql-header"
        value="2"
        aria-label="Heading 2"
      >
        H2
      </button>
    </span>
    <span className="ql-formats">
      <select className="ql-size" defaultValue="" aria-label="Text size">
        <option value="small">Small</option>
        <option value="">Normal</option>
        <option value="large">Large</option>
        <option value="huge">Huge</option>
      </select>
    </span>
    <span className="ql-formats cms-articles-toolbar-divider">
      <button
        type="button"
        className="ql-list"
        value="ordered"
        aria-label="Numbered list"
      />
      <button
        type="button"
        className="ql-list"
        value="bullet"
        aria-label="Bullet list"
      />
      <button
        type="button"
        className="ql-indent"
        value="-1"
        aria-label="Decrease indent"
      />
      <button
        type="button"
        className="ql-indent"
        value="+1"
        aria-label="Increase indent"
      />
      <button type="button" className="ql-link" aria-label="Add link" />
      <button
        type="button"
        className="ql-script"
        value="super"
        aria-label="Superscript"
      />
      <button
        type="button"
        className="ql-script"
        value="sub"
        aria-label="Subscript"
      />
    </span>
  </div>
);

const ModalCloseIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="19"
    height="19"
    viewBox="0 0 19 19"
    fill="none"
    aria-hidden="true"
    focusable="false"
  >
    <path
      d="M1.46154 19L0 17.5385L8.03846 9.5L0 1.46154L1.46154 0L9.5 8.03846L17.5385 0L19 1.46154L10.9615 9.5L19 17.5385L17.5385 19L9.5 10.9615L1.46154 19Z"
      fill="black"
    />
  </svg>
);

const defaultArticleForm = {
  title: "",
  summary: "",
  tag: [],
  submittedBy: "",
  profileLink: "",
  content: "",
  imageName: "",
  imageExtension: "",
  imageError: "",
  isActive: true,
  isUploadingImage: false,
  mandatoryFields: false,
  errors: {
    title: { error: false, message: "" },
    summary: { error: false, message: "" },
    submittedBy: { error: false, message: "" },
    profileLink: { error: false, message: "" },
    content: { error: false, message: "" },
    image: { error: false, message: "" }
  }
};

const hasTextValue = value => String(value || "").trim().length > 0;

const hasQuillContent = value =>
  String(value || "")
    .replace(/<(.|\n)*?>/g, " ")
    .replace(/&nbsp;/g, " ")
    .trim().length > 0;

const normalizeTagValue = tag => {
  if (!tag || typeof tag !== "object") {
    return tag === undefined || tag === null ? "" : String(tag);
  }

  const value =
    tag.value || tag.id || tag.tagId || tag.tag_id || tag.name || tag.label;

  return value === undefined || value === null ? "" : String(value);
};

const getComparableArticleForm = form => ({
  title: String((form && form.title) || ""),
  summary: String((form && form.summary) || ""),
  submittedBy: String((form && form.submittedBy) || ""),
  profileLink: String((form && form.profileLink) || ""),
  content: String((form && form.content) || ""),
  imageName: String((form && form.imageName) || ""),
  imageExtension: String((form && form.imageExtension) || ""),
  imageId:
    form && form.imageId !== undefined && form.imageId !== null
      ? String(form.imageId)
      : "",
  isActive:
    form && form.isActive !== undefined && form.isActive !== null
      ? Boolean(form.isActive)
      : true,
  tag: Array.isArray(form && form.tag)
    ? form.tag.map(normalizeTagValue).filter(Boolean)
    : []
});

const areComparableFormsEqual = (firstForm, secondForm) =>
  JSON.stringify(getComparableArticleForm(firstForm)) ===
  JSON.stringify(getComparableArticleForm(secondForm));

const AddArticleModal = ({
  show,
  onHide,
  articleForm,
  onFormFieldChange,
  tags,
  onArticleImage,
  onArticleImageError,
  onArticlePreview,
  onArticleSubmit,
  onRemoveArticleImage,
  isEdit
}) => {
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const initialEditFormRef = useRef(null);
  const safeArticleForm = {
    ...defaultArticleForm,
    ...articleForm,
    errors: {
      ...defaultArticleForm.errors,
      ...(articleForm && articleForm.errors ? articleForm.errors : {})
    }
  };

  useEffect(() => {
    if (show) {
      setIsPreviewMode(false);
      initialEditFormRef.current = isEdit
        ? getComparableArticleForm({
            ...defaultArticleForm,
            ...articleForm
          })
        : null;
    } else {
      initialEditFormRef.current = null;
    }
  }, [show, isEdit, articleForm && articleForm.id]);

  const getPreviewDate = () => {
    const date = new Date();
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();

    return `${day}-${month}-${year}`;
  };

  const getPreviewArticle = () => ({
    title: safeArticleForm.title,
    createdAt: getPreviewDate(),
    submittedByName: safeArticleForm.submittedBy,
    tags: selectedTags.map(tag => tag.label || tag.value).filter(Boolean),
    image: safeArticleForm.image,
    articleContent: safeArticleForm.content
  });
  const tagOptions = (tags || [])
    .map(tag =>
      tag && typeof tag === "object"
        ? tag
        : {
            label: tag,
            value: tag
          }
    )
    .filter(
      tag =>
        tag &&
        tag.label &&
        tag.value !== undefined &&
        tag.value !== null &&
        tag.value !== ""
    );
  const selectedTags = Array.isArray(safeArticleForm.tag)
    ? safeArticleForm.tag
    : [];
  const tagSelectStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: 46,
      border: state.isFocused ? "2px solid #1677ff" : "1px solid #c9d0db",
      borderRadius: 4,
      boxShadow: "none",
      background: "#fff",
      fontSize: 14,
      fontFamily: "mpsb",
      color: "#2f2f2f",
      cursor: "text",
      outline: "none",
      "&:hover": {
        borderColor: "#1677ff"
      }
    }),
    placeholder: base => ({
      ...base,
      color: "#2b5d85",
      fontSize: 15,
      fontFamily: "mpsb"
    }),
    dropdownIndicator: base => ({
      ...base,
      color: "#5e5e5e",
      padding: "0 10px",
      "&:hover": {
        color: "#5e5e5e"
      }
    }),
    clearIndicator: base => ({
      ...base,
      color: "#6c6c6c",
      padding: "0 10px",
      cursor: "pointer",
      "&:hover": {
        color: "#6c6c6c",
        cursor: "pointer"
      }
    }),
    indicatorSeparator: base => ({
      ...base,
      backgroundColor: "#d8d8d8",
      marginBottom: 6,
      marginTop: 6
    }),
    singleValue: base => ({
      ...base,
      color: "#2f2f2f",
      fontSize: 14,
      fontFamily: "mpsb"
    }),
    valueContainer: base => ({
      ...base,
      minHeight: 42,
      padding: "4px 10px",
      gap: 4
    }),
    input: base => ({
      ...base,
      margin: 0,
      padding: 0,
      fontSize: 14,
      fontFamily: "mpsb"
    }),
    menuPortal: base => ({
      ...base,
      zIndex: 9999
    }),
    menu: base => ({
      ...base,
      borderRadius: 3,
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.16)",
      marginTop: 6,
      overflow: "hidden"
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isFocused || state.isSelected ? "#d8e6f7" : "#fff",
      color: "#2b5d85",
      cursor: "pointer",
      fontFamily: "mpsb",
      fontSize: 14,
      padding: "8px 12px",
      "&:active": {
        backgroundColor: "#d8e6f7"
      }
    }),
    multiValue: base => ({
      ...base,
      alignItems: "center",
      backgroundColor: "#eeeeee",
      borderRadius: 2,
      minHeight: 26,
      margin: "2px 0",
      overflow: "hidden"
    }),
    multiValueLabel: base => ({
      ...base,
      color: "#1f1f1f",
      fontFamily: "mpsb",
      fontSize: 13,
      lineHeight: "18px",
      padding: "4px 6px 4px 8px"
    }),
    multiValueRemove: base => ({
      ...base,
      alignItems: "center",
      alignSelf: "stretch",
      borderRadius: 0,
      color: "#2b5d85",
      cursor: "pointer",
      display: "flex",
      paddingLeft: 5,
      paddingRight: 5,
      "&:hover": {
        backgroundColor: "#ffad9f",
        color: "#b13a2b"
      }
    })
  };

  const hasEnteredArticleData = () => {
    const {
      title,
      summary,
      tag,
      submittedBy,
      profileLink,
      content,
      imageName,
      imageExtension,
      isUploadingImage
    } = safeArticleForm;

    if (
      hasTextValue(title) ||
      hasTextValue(summary) ||
      hasTextValue(submittedBy) ||
      hasTextValue(profileLink) ||
      hasQuillContent(content) ||
      hasTextValue(imageName) ||
      hasTextValue(imageExtension) ||
      isUploadingImage
    ) {
      return true;
    }

    if (Array.isArray(tag) && tag.length > 0) {
      return true;
    }

    return false;
  };

  const hasArticleErrorState = () => {
    const { imageError, mandatoryFields, errors } = safeArticleForm;

    if (hasTextValue(imageError) || mandatoryFields) {
      return true;
    }

    return (
      errors &&
      Object.values(errors).some(
        error => error && (error.error || hasTextValue(error.message))
      )
    );
  };

  const shouldConfirmBeforeClose = () =>
    isEdit
      ? !areComparableFormsEqual(initialEditFormRef.current, safeArticleForm)
      : hasEnteredArticleData() || hasArticleErrorState();

  const handleClose = () => {
    if (
      !shouldConfirmBeforeClose() ||
      window.confirm(
        "You have unsaved changes, are you sure you want to leave?"
      )
    ) {
      onHide && onHide();
    }
  };

  // const handlePreviewSubmit = event => {
  //   const canPreview = onArticlePreview ? onArticlePreview(event) : true;

  //   if (canPreview) {
  //     setIsPreviewMode(true);
  //   }
  // };
  const handlePreviewSubmit = event => {
    if (!safeArticleForm.isActive) {
      onArticleSubmit();
      return;
    }

    const canPreview = onArticlePreview ? onArticlePreview(event) : true;

    if (canPreview) {
      setIsPreviewMode(true);
    }
  };
  const handleBackToForm = () => {
    setIsPreviewMode(false);
  };

  return (
    <>
      <LoaderPage loading={safeArticleForm.isUploadingImage} />
      <Modal
        show={show}
        onHide={handleClose}
        dialogClassName="cms-articles-form-dialog"
        className="cms-articles-form-modal"
      >
        <div
          className={`cms-articles-form-shell ${
            isPreviewMode ? "cms-articles-preview-shell" : ""
          }`}
        >
          <div className="cms-articles-form-header">
            <h2 className="cms-articles-form-title">
              {isPreviewMode
                ? "Article Preview"
                : isEdit
                ? "Edit Article"
                : "Add Article"}
            </h2>
            <button
              type="button"
              className="cms-articles-close"
              onClick={handleClose}
            >
              <ModalCloseIcon />
            </button>
          </div>
          {isPreviewMode ? (
            <>
              <div className="cms-articles-preview-body">
                <PreviewArticle article={getPreviewArticle()} />
              </div>
              <div className="cms-articles-preview-footer">
                <Button
                  type="button"
                  className="cms-articles-preview-back-btn"
                  onClick={handleBackToForm}
                >
                  <img src={BackIcon} alt="" aria-hidden="true" />
                  Back
                </Button>
                <Button
                  type="button"
                  className="cms-articles-preview-submit-btn"
                  onClick={onArticleSubmit}
                >
                  Submit
                </Button>
              </div>
            </>
          ) : (
            <div className="cms-articles-form-body">
              <Form.Group>
                <div className="cms-articles-title-label-row">
                  <Form.Label className="label-style">
                    Title<span className="required">*</span>
                  </Form.Label>
                  {isEdit ? (
                    <div className="d-flex mt-2 mr-n2">
                      <p
                        className={`mb-0 mr-n3 pr-1 ${
                          safeArticleForm.isActive
                            ? "toggle-active"
                            : "toggle-inactive"
                        }`}
                      >
                        {safeArticleForm.isActive ? "Active" : "Inactive"}
                      </p>
                      <Form.Group
                        controlId="cms-articles-edit-active-toggle"
                        className="mb-0 ml-3 mr-2"
                      >
                        <Form.Check
                          type="checkbox"
                          label={<span />}
                          checked={Boolean(safeArticleForm.isActive)}
                          onChange={event =>
                            onFormFieldChange("isActive", event.target.checked)
                          }
                          className={`form-toggle ${
                            safeArticleForm.isActive ? "checked" : "unchecked"
                          } active-user`}
                        />
                      </Form.Group>
                    </div>
                  ) : null}
                </div>
                <Form.Control
                  type="text"
                  placeholder="Enter Title"
                  isInvalid={
                    safeArticleForm.mandatoryFields &&
                    safeArticleForm.errors.title.error
                  }
                  className="addFormText cms-articles-input"
                  value={safeArticleForm.title}
                  onChange={event =>
                    onFormFieldChange("title", event.target.value)
                  }
                />
                {safeArticleForm.mandatoryFields &&
                safeArticleForm.errors.title.error ? (
                  <Form.Control.Feedback type="invalid">
                    {safeArticleForm.errors.title.message}
                  </Form.Control.Feedback>
                ) : null}
              </Form.Group>

              {/* <Form.Group className="cms-articles-form-group-gap">
            <Form.Label className="label-style">
              Short Summary<span className="required">*</span>
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              maxLength={400}
              placeholder="Enter Short Summary"
              isInvalid={
                safeArticleForm.mandatoryFields &&
                safeArticleForm.errors.summary.error
              }
              className="addFormText cms-articles-input cms-articles-textarea"
              value={safeArticleForm.summary}
              onChange={event =>
                onFormFieldChange("summary", event.target.value)
              }
            />
            {safeArticleForm.mandatoryFields &&
            safeArticleForm.errors.summary.error ? (
              <Form.Control.Feedback type="invalid">
                {safeArticleForm.errors.summary.message}
              </Form.Control.Feedback>
            ) : null}
          </Form.Group> */}

              <Form.Group className="cms-articles-form-group-gap">
                <Form.Label className="label-style">Tag</Form.Label>
                <Select
                  styles={tagSelectStyles}
                  isMulti
                  name="tag"
                  placeholder="Select Tag"
                  options={tagOptions}
                  className="basic-multi-select cms-articles-tag-select"
                  classNamePrefix="select"
                  onChange={value => onFormFieldChange("tag", value || [])}
                  value={selectedTags}
                  isClearable
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
              </Form.Group>

              <Form.Group className="cms-articles-form-group-gap">
                <Form.Label className="label-style">
                  Submitted By<span className="required">*</span>
                </Form.Label>
                <div className="cms-articles-submitted-block">
                  <Form.Control
                    type="text"
                    placeholder="Enter User name"
                    isInvalid={
                      safeArticleForm.mandatoryFields &&
                      safeArticleForm.errors.submittedBy.error
                    }
                    className="addFormText cms-articles-input cms-articles-submitted-input"
                    value={safeArticleForm.submittedBy}
                    onChange={event =>
                      onFormFieldChange("submittedBy", event.target.value)
                    }
                  />
                  <div className="cms-articles-link-row">
                    <div
                      className="cms-articles-linkedin-box"
                      aria-hidden="true"
                    >
                      <Image
                        src={LinkedinIcon}
                        className="cms-articles-linkedin-icon"
                      />
                    </div>
                    <Form.Control
                      type="text"
                      placeholder="Enter Profile link"
                      className="addFormText cms-articles-input cms-articles-link-field"
                      value={safeArticleForm.profileLink}
                      onChange={event =>
                        onFormFieldChange("profileLink", event.target.value)
                      }
                      onBlur={event =>
                        onFormFieldChange("profileLink", event.target.value)
                      }
                      isInvalid={safeArticleForm.errors.profileLink.error}
                    />
                    <div
                      className={`cms-articles-link-validation-icon ${
                        safeArticleForm.profileLink
                          ? safeArticleForm.errors.profileLink.error
                            ? "invalid"
                            : "valid"
                          : ""
                      }`}
                    >
                      {safeArticleForm.profileLink
                        ? safeArticleForm.errors.profileLink.error
                          ? "✖"
                          : "✔"
                        : null}
                    </div>
                  </div>
                </div>
                {safeArticleForm.mandatoryFields &&
                safeArticleForm.errors.submittedBy.error ? (
                  <Form.Control.Feedback type="invalid" className="d-block">
                    {safeArticleForm.errors.submittedBy.message}
                  </Form.Control.Feedback>
                ) : null}
                {safeArticleForm.errors.profileLink.error ? (
                  <Form.Control.Feedback type="invalid" className="d-block">
                    {safeArticleForm.errors.profileLink.message}
                  </Form.Control.Feedback>
                ) : null}
              </Form.Group>

              <Form.Group className="cms-articles-form-group-gap">
                <Form.Label className="label-style">
                  Article Content<span className="required">*</span>
                </Form.Label>
                <div
                  className={`cms-articles-editor quill-wrapper ${
                    safeArticleForm.mandatoryFields &&
                    safeArticleForm.errors.content.error
                      ? "is-invalid"
                      : ""
                  }`}
                >
                  <ArticleContentToolbar />
                  <ReactQuill
                    theme="snow"
                    modules={quillModules}
                    formats={quillFormats}
                    value={safeArticleForm.content}
                    onChange={value => onFormFieldChange("content", value)}
                    placeholder="Start writing your thoughts...."
                  />
                </div>
                {safeArticleForm.mandatoryFields &&
                safeArticleForm.errors.content.error ? (
                  <div className="invalidDesignation">
                    {safeArticleForm.errors.content.message}
                  </div>
                ) : null}
              </Form.Group>

              <Form.Group className="cms-articles-form-group-gap">
                <Form.Label className="label-style">
                  Upload Image<span className="required">*</span>
                </Form.Label>
                {safeArticleForm.imageName ? (
                  <div className="d-flex showImage">
                    <div
                      className="d-flex flex-column justify-content-center align-items-center imgIcon"
                      style={{ backgroundImage: `url(${ImageBackground})` }}
                    >
                      <p className="text-white text-uppercase mt-4 iconText">
                        {safeArticleForm.imageExtension || ""}
                      </p>
                    </div>
                    <div className="image uploadedPhoto">
                      {safeArticleForm.imageName.length > 20
                        ? safeArticleForm.imageName
                            .split(".", 1)[0]
                            .substr(0, 20) + "..."
                        : safeArticleForm.imageName.split(".", 1)[0]}
                    </div>
                    <div className="d-flex align-items-start imageUploaded">
                      <img
                        className="cancelImg"
                        src={CancleIcon}
                        alt="Cancel_Icon"
                        onClick={onRemoveArticleImage}
                      />
                    </div>
                  </div>
                ) : (
                  <div>
                    <Files
                      onChange={onArticleImage}
                      onError={onArticleImageError}
                      multiple={false}
                      accepts={IMAGE_FORMAT}
                      maxFileSize={FILE_SIZE_LIMIT.OTHER_FORMATS}
                      className="best-practices-add-files cms-add-files"
                      data-test="article-add-files"
                      clickable
                    >
                      <div>
                        <Image
                          src={IconPlus}
                          className="addFile_plus"
                          alt="Plus Icon"
                        />
                        <div
                          className="add-file-text pt-2"
                          data-test="label-add-document"
                        >
                          Add
                        </div>
                      </div>
                    </Files>
                    <p className="add-file-subtext font-semibold pt-1 mb-0 font-italic-cms cms-articles-upload-meta">
                      Supported image types: .png, .jpg, .jpeg <br />
                      Recommended dimensions: 1340 x 400 px
                      <br />
                      Max image size: 10MB
                    </p>
                  </div>
                )}
                {safeArticleForm.mandatoryFields &&
                safeArticleForm.errors.image.error ? (
                  <div className="invalidDesignation">
                    {safeArticleForm.errors.image.message}
                  </div>
                ) : safeArticleForm.imageError ? (
                  <div className="invalidDesignation">
                    {safeArticleForm.imageError}
                  </div>
                ) : null}
              </Form.Group>

              <Button
                className="cms-articles-submit-btn"
                onClick={
                  safeArticleForm.isActive
                    ? handlePreviewSubmit
                    : onArticleSubmit
                }
              >
                {safeArticleForm.isActive ? "PREVIEW & SUBMIT" : "SUBMIT"}
              </Button>
            </div>
          )}
        </div>
      </Modal>
    </>
  );
};

export default AddArticleModal;
